import { DeleteOutlined } from "@ant-design/icons";
import { Dropdown, MenuProps, Modal } from "antd";
import { message } from "utils/ciq-message";
import { useCIQMutation } from "hooks/ciq-gql-hooks";
import {
  MUTATION_DELETE_PROJECT,
  MUTATION_UPDATE_PROJECT_STATUS
} from "services/graphQL/mutations";
import { EProjectStatusId } from "constants/index";
import MoreVerticalIcon from "components/svg-icons/more-vertical-icon";
import ArchiveIcon from "components/svg-icons/archive-icon";

type TProjectActionsData = {
  id?: string;
  name?: string;
  project_status_id?: number | null;
};

const ACTION_LINK_STYLE = { color: "var(--ciq-action-link)" };
// Not an antd `danger` item: that swaps the hover background for a red one,
// while both menu items should share the neutral hover.
const DANGER_STYLE = { color: "var(--ciq-danger)" };
const PLAIN_BUTTON_CLASS =
  "m-0 cursor-pointer border-0 bg-transparent p-0 [font-family:inherit] outline-none focus:outline-none focus-visible:outline-none";

type TProjectActionsProps = {
  data?: TProjectActionsData;
  onProjectChanged: () => void;
};

const openConfirm = (config: {
  title: string;
  content: React.ReactNode;
  okText: string;
  danger?: boolean;
  onOk: () => Promise<void>;
}) => {
  Modal.confirm({
    title: config.title,
    content: config.content,
    okText: config.okText,
    cancelText: "Cancel",
    okButtonProps: config.danger ? { danger: true } : undefined,
    icon: null,
    closable: true,
    centered: true,
    onOk: config.onOk
  });
};

/**
 * Actions column of the subscription Projects list (Subscription Admins only):
 * Archive / Delete for active projects, Restore for archived ones.
 */
function ProjectActionsCell({ data, onProjectChanged }: TProjectActionsProps) {
  const [deleteProject] = useCIQMutation(MUTATION_DELETE_PROJECT);
  const [updateProjectStatus] = useCIQMutation(MUTATION_UPDATE_PROJECT_STATUS);

  if (!data?.id) return null;
  const projectId = data.id;
  const projectName = data.name ?? "";

  const updateArchived = async (archived: boolean) => {
    const response = await updateProjectStatus({
      variables: {
        projectId,
        statusId: archived ? EProjectStatusId.Archived : EProjectStatusId.Active
      }
    });
    if (!response?.success) return;
    if (!response.data?.update_project?.affected_rows) {
      message.error(
        archived
          ? "Project could not be archived. Please try again."
          : "Project could not be restored. Please try again."
      );
      return;
    }
    message.success(
      archived
        ? "Project archived successfully."
        : "Project restored successfully."
    );
    onProjectChanged();
  };

  const confirmArchive = () =>
    openConfirm({
      title: "Archive this project?",
      content: (
        <p className="m-0" data-testid="project-actions-archive-content">
          Once archived, no changes can be made to this project. You can restore
          this anytime from Archived Projects.
        </p>
      ),
      okText: "Archive",
      onOk: () => updateArchived(true)
    });

  const confirmRestore = () =>
    openConfirm({
      title: "Restore this project?",
      content: (
        <p className="m-0" data-testid="project-actions-restore-content">
          Once restored, changes can be made to the project again.
        </p>
      ),
      okText: "Restore",
      onOk: () => updateArchived(false)
    });

  const confirmDelete = () =>
    openConfirm({
      title: "Delete this project?",
      content: (
        <p className="m-0" data-testid="project-actions-delete-content">
          Are you sure you want to delete selected project &apos;{projectName}
          &apos;? Deleting this project will delete all its contents.
        </p>
      ),
      okText: "Delete",
      danger: true,
      onOk: async () => {
        const response = await deleteProject({
          variables: { projectIds: [projectId] }
        });
        if (response?.success) {
          message.success("Project deleted successfully.");
          onProjectChanged();
        }
      }
    });

  if (data.project_status_id === EProjectStatusId.Archived) {
    return (
      <div className="flex h-full w-full items-center justify-center">
        <button
          type="button"
          className={`${PLAIN_BUTTON_CLASS} text-sm font-normal`}
          style={ACTION_LINK_STYLE}
          onClick={confirmRestore}
          data-testid="project-actions-restore"
        >
          Restore Project
        </button>
      </div>
    );
  }

  const menu: MenuProps = {
    items: [
      {
        key: "archive",
        label: (
          <span
            className="flex items-center gap-2 font-semibold"
            data-testid="project-actions-archive"
          >
            <ArchiveIcon />
            Archive Project
          </span>
        )
      },
      {
        key: "delete",
        label: (
          <span
            className="flex items-center gap-2 font-semibold"
            style={DANGER_STYLE}
            data-testid="project-actions-delete"
          >
            <DeleteOutlined
              className="flex w-5 justify-center text-base"
              style={DANGER_STYLE}
            />
            Delete Project
          </span>
        )
      }
    ],
    onClick: ({ key }) => {
      if (key === "archive") confirmArchive();
      if (key === "delete") confirmDelete();
    }
  };

  return (
    <div className="flex h-full w-full items-center justify-center">
      <Dropdown menu={menu} trigger={["click"]} placement="bottomRight">
        <button
          type="button"
          className={`${PLAIN_BUTTON_CLASS} flex leading-none`}
          aria-label={`Actions for ${projectName}`}
          data-testid="project-actions-menu"
        >
          <MoreVerticalIcon />
        </button>
      </Dropdown>
    </div>
  );
}

export default ProjectActionsCell;
